import { GeoJsonLayer, BitmapLayer, PathLayer } from "@deck.gl/layers";
import { ScenegraphLayer } from "@deck.gl/mesh-layers";
import { TileLayer } from "@deck.gl/geo-layers";
import proj4 from "proj4";
import bbox from "@turf/bbox";
import bboxPolygon from "@turf/bbox-polygon";
import { BASE_URL } from "../../config";
import { getAccessToken } from "../LoginForm/Auth/auth";
import { showMapConfirmToast } from "../Toast/mapToast";

const GEO_SERVER_URL_TRAFO = `${BASE_URL}/geoserver/elektro/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=elektro:trafo_stanice&outputFormat=application/json`;
const GEO_SERVER_URL_STUBOVI = `${BASE_URL}/geoserver/elektro/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=elektro:stubovi&outputFormat=application/json`;
const GEO_SERVER_URL_MJERNA_MJESTA = `${BASE_URL}/geoserver/elektro/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=elektro:mjerna_mjesta&outputFormat=application/json`;
const GEO_SERVER_URL_VOD = `${BASE_URL}/geoserver/elektro/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=elektro:vod&outputFormat=application/json`;
const GEO_SERVER_URL_DIONICA = `${BASE_URL}/geoserver/elektro/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=elektro:dionica&outputFormat=application/json`;
const GEO_SERVER_URL_OCITACKI_HOD = `${BASE_URL}/geoserver/elektro/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=elektro:ocitacki_hod&outputFormat=application/json`;

proj4.defs(
    "EPSG:8678",
    "+proj=tmerc +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=682,-203,480,0,0,0,0 +units=m +no_defs +type=crs"
);

const getColorForValueInSubstations = (value) => {
    if (value >= 0 && value <= 50) return [11, 12, 8, 255];
    if (value > 50 && value <= 150) return [46, 73, 43, 255];
    if (value > 150 && value <= 400) return [88, 150, 93, 255];
    if (value > 400) return [185, 240, 172, 255];

    return [255, 255, 255, 255];
};

const transformCoordinates = (coordinates) => {
    const [x, y] = coordinates;
    const [lon, lat] = proj4("EPSG:8678", "EPSG:4326", [x, y]);
    const z = coordinates.length > 2 ? coordinates[2] : 0;

    return [lon, lat, z];
};

const transformGeometry = (geometry) => {
    if (!geometry) return geometry;

    if (geometry.type === "Point") {
        geometry.coordinates = transformCoordinates(geometry.coordinates);
    } else if (geometry.type === "LineString") {
        geometry.coordinates = geometry.coordinates.map((coord) =>
            transformCoordinates(coord)
        );
    } else if (geometry.type === "MultiLineString" || geometry.type === "Polygon") {
        geometry.coordinates = geometry.coordinates.map((line) =>
            line.map((coord) => transformCoordinates(coord))
        );
    } else if (geometry.type === "MultiPolygon") {
        geometry.coordinates = geometry.coordinates.map((polygon) =>
            polygon.map((ring) =>
                ring.map((coord) => transformCoordinates(coord))
            )
        );
    } else if (geometry.type === "MultiPoint") {
        geometry.coordinates = geometry.coordinates.map((coord) =>
            transformCoordinates(coord)
        );
    }

    return geometry;
};

const transformGeoJSON = (geojson) => {
    if (geojson.type === "FeatureCollection") {
        geojson.features.forEach((feature) => {
            feature.geometry = transformGeometry(feature.geometry);
        });
    }

    return geojson;
};

export const fetchYearsForSlider = async () => {
    const urls = [
        GEO_SERVER_URL_STUBOVI,
        GEO_SERVER_URL_TRAFO,
        GEO_SERVER_URL_VOD,
    ];

    const years = new Set();

    for (const url of urls) {
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${getAccessToken()}`,
            },
        });

        const geojson = await response.json();

        geojson.features.forEach((feature) => {
            if (feature.properties.datum_upisa) {
                const year = new Date(feature.properties.datum_upisa).getFullYear();

                if (!isNaN(year)) {
                    years.add(year);
                }
            }
        });
    }

    return Array.from(years).sort((a, b) => a - b);
};

const expandBoundingBox = (boundingBox, margin) => {
    const [minX, minY, maxX, maxY] = boundingBox;
    return [minX - margin, minY - margin, maxX + margin, maxY + margin];
};

const transformBoundingBox = (boundingBox) => {
    const expandedBoundingBox = expandBoundingBox(boundingBox, 100);
    const [minX, minY, maxX, maxY] = expandedBoundingBox;

    const transformedMin = proj4("EPSG:8678", "EPSG:4326", [minX, minY]);
    const transformedMax = proj4("EPSG:8678", "EPSG:4326", [maxX, maxY]);

    return [...transformedMin, ...transformedMax];
};

export const addLayer = async (
    layerName,
    setHoverInfo,
    handleClickInfo,
    filterYear,
    setWmsLayers,
    networkType,
    viewState,
    setViewState,
    getAll,
    setOverlay,
    id_terenske_jedinice,
    cqlFilterParam,
    isDrawing
) => {
    const token = getAccessToken();

    if (setOverlay) {
        setOverlay({
            active: true,
            message: "Učitavanje sloja, molimo sačekajte...",
        });
    }

    let geoServerUrl;

    if (layerName === "stubovi") geoServerUrl = GEO_SERVER_URL_STUBOVI;
    else if (layerName === "trafo_stanice") geoServerUrl = GEO_SERVER_URL_TRAFO;
    else if (layerName === "mjerna_mjesta") geoServerUrl = GEO_SERVER_URL_MJERNA_MJESTA;
    else if (layerName === "vod") geoServerUrl = GEO_SERVER_URL_VOD;
    else if (layerName === "dionica") geoServerUrl = GEO_SERVER_URL_DIONICA;
    else if (layerName === "ocitacki_hod") geoServerUrl = GEO_SERVER_URL_OCITACKI_HOD;
    else return null;

    const filters = [];

    if (filterYear) {
        filters.push(
            `datum_upisa >= '${filterYear}-01-01' AND datum_upisa <= '${filterYear}-12-31'`
        );
    }

    if (id_terenske_jedinice) {
        filters.push(`id_terenske_jedinice = ${id_terenske_jedinice}`);
    }

    if (!getAll) {
        filters.push("datum_prestanka IS NULL");
    }

    const layersWithNetworkType = ["stubovi", "vod", "dionica"];

    if (networkType && layersWithNetworkType.includes(layerName)) {
        filters.push(`tip_mreze = '${networkType}'`);
    }

    if (typeof cqlFilterParam === "string" && cqlFilterParam.trim().length > 0) {
        filters.push(`(${cqlFilterParam.trim()})`);
    }

    const originalCqlFilter = filters.join(" AND ");
    const finalCqlFilter =
        filters.length > 0
            ? `&CQL_FILTER=${encodeURIComponent(originalCqlFilter)}`
            : "";

    setWmsLayers((prevLayers) => {
        const prefix = networkType ? `${layerName}-${networkType}` : layerName;
        const updatedLayers = prevLayers.filter(
            (layer) => !layer.id.startsWith(`${prefix}-`)
        );
        const suffix = networkType ? `${layerName}-${networkType}` : layerName;

        return [
            ...updatedLayers,
            new TileLayer({
                id: `${suffix}-${Date.now()}`,
                data: `${BASE_URL}/geoserver/wms`,
                visible: true,
                source: isDrawing ? "drawing" : "internal",
                originalCqlFilter,
                minZoom: 0,
                maxZoom: 23,
                tileSize: 256,

                renderSubLayers: (props) => {
                    const {
                        bbox: { west, south, east, north },
                    } = props.tile;

                    return new BitmapLayer(props, {
                        data: null,
                        image: props.data,
                        bounds: [west, south, east, north],
                        bbox: [west, south, east, north],
                    });
                },

                getTileData: ({ bbox }) => {
                    const { west, south, east, north } = bbox;

                    const tileUrl =
                        `${BASE_URL}/geoserver/wms?service=WMS&request=GetMap&version=1.1.1` +
                        `&layers=elektro:${layerName}` +
                        `&styles=` +
                        `&format=image/png` +
                        `&transparent=true` +
                        `&height=256` +
                        `&width=256` +
                        `&srs=EPSG:4326` +
                        `&bbox=${west},${south},${east},${north}` +
                        `${finalCqlFilter}`;

                    return fetch(tileUrl, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    })
                        .then((response) => {
                            if (!response.ok) {
                                return response.text().then((text) => {
                                    throw new Error(`Failed to fetch tile. Response: ${text}`);
                                });
                            }

                            return response.blob();
                        })
                        .then((blob) => URL.createObjectURL(blob));
                },
            }),
        ];
    });

    try {
        const response = await fetch(geoServerUrl + finalCqlFilter, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error(
                `Greška pri učitavanju sloja ${layerName}: ${response.statusText}`
            );
        }

        const geojson = await response.json();

        if (!geojson.features || geojson.features.length === 0) {
            if (setOverlay) {
                setOverlay({ active: false, message: "" });
            }

            const layerLabels = {
                stubovi: "Stubovi",
                trafo_stanice: "Trafostanice",
                mjerna_mjesta: "Mjerna mjesta",
                vod: "Vod",
                dionica: "Dionica",
                ocitacki_hod: "Očitački hod",
            };

            const label = layerLabels[layerName] || layerName;

            showMapConfirmToast({
                title: "Sloj bez objekata",
                message: `Sloj "${label}" nema nijedan objekat za zadati filter, ali će biti prikazan.`,
                confirmText: "U redu",
                cancelText: "",
                variant: "warning",
                autoClose: 3000,
                showTimer: true,
            });
        }

        const transformedGeoJSON = transformGeoJSON(geojson);

        if (transformedGeoJSON.features.length > 0) {
            const boundingBox = bbox(transformedGeoJSON);
            const transformedBoundingBox = transformBoundingBox(boundingBox);
            const [minX, minY, maxX, maxY] = transformedBoundingBox;

            const centerLongitude = (minX + maxX) / 2;
            const centerLatitude = (minY + maxY) / 2;

            showMapConfirmToast({
                title: "Pozicioniranje mape",
                message: "Da li želite da se prikaz mape pozicionira prema novom sloju?",
                confirmText: "Da",
                cancelText: "Ne",
                variant: "info",
                autoClose: 3000,
                showTimer: true,
                onConfirm: () => {
                    setViewState((prev) => ({
                        ...prev,
                        longitude: centerLongitude,
                        latitude: centerLatitude,
                        zoom: prev.zoom,
                        pitch: prev.pitch,
                        bearing: prev.bearing,
                        transitionDuration: 1000,
                    }));
                },
            });
        }

        let layer;

        if (
            viewState.is3D &&
            (
                transformedGeoJSON.features[0]?.geometry?.type === "Point" ||
                transformedGeoJSON.features[0]?.geometry?.type === "MultiPoint"
            )
        ) {
            let scenegraphModel = "";

            if (layerName === "stubovi") {
                scenegraphModel = "/models/27.glb";
            } else if (layerName === "trafo_stanice") {
                scenegraphModel = "/models/3.glb";
            } else if (layerName === "mjerna_mjesta") {
                scenegraphModel = "/models/Elektricno_brojilo.glb";
            }

            if (setOverlay) {
                setOverlay({ active: false, message: "" });
            }

            return new ScenegraphLayer({
                id: `${layerName}${networkType ? `-${networkType}-layer` : "-layer"}`,
                data: transformedGeoJSON.features,
                source: "internal",
                networkType: networkType || "",
                originalCqlFilter,
                scenegraph: scenegraphModel,
                getPosition: (d) => d.geometry.coordinates,
                getOrientation: [0, 0, 90],
                sizeScale: 1,
                pickable: true,
                loadOptions: {
                    gltf: {
                        decompressMeshes: true,
                        postProcess: true,
                    },
                },
                onHover: (info) => setHoverInfo(info),
                onClick: (info) => handleClickInfo(info),
            });
        }

        if (viewState.is3D && (layerName === "vod" || layerName === "dionica")) {
            const paths = transformedGeoJSON.features.flatMap((feature, i) => {
                if (!feature.geometry?.coordinates) return [];

                return feature.geometry.coordinates
                    .filter((segment) => segment.every((coord) => coord.length >= 2))
                    .map((segment, j) => ({
                        path: segment.map(([x, y]) => [x, y, 5]),
                        properties: feature.properties,
                        id: `${feature.id || i}-${j}`,
                    }));
            });

            layer = new PathLayer({
                id: `${layerName}${networkType ? `-${networkType}-layer` : "-layer"}`,
                data: paths,
                source: "internal",
                networkType: networkType || "",
                originalCqlFilter,
                getPath: (d) => d.path,
                getWidth: () => {
                    const z = viewState.zoom;

                    if (layerName === "vod") {
                        if (z > 16) return 6;
                        if (z > 14) return 4;
                        if (z > 12) return 2;
                        return 1;
                    }

                    return 1;
                },
                widthUnits: "pixels",
                widthMinPixels: 1,
                widthMaxPixels: 8,
                getColor: [255, 165, 0, 255],
                pickable: true,
                autoHighlight: true,
                highlightColor: [0, 0, 255, 150],
                onHover: setHoverInfo,
                onClick: handleClickInfo,
            });
        } else if (!viewState.is3D) {
            layer = new GeoJsonLayer({
                id: `${layerName}${networkType ? `-${networkType}-layer` : "-layer"}`,
                data: transformedGeoJSON,
                source: "internal",
                networkType: networkType || "",
                originalCqlFilter,
                filled: layerName !== "vod",

                getFillColor: (feature) => {
                    const tipMreze = feature.properties.tip_mreze;

                    if (layerName === "stubovi") {
                        if (tipMreze === "niskonaponska") return [255, 0, 0, 200];
                        if (tipMreze === "visokonaponska") return [0, 220, 0, 200];
                        return [128, 128, 128, 200];
                    }

                    if (layerName === "trafo_stanice") {
                        if (tipMreze === "niskonaponska") return [0, 150, 255, 180];
                        if (tipMreze === "visokonaponska") return [180, 0, 0, 180];

                        const value = feature.properties.broj_mjernih_mjesta;
                        return getColorForValueInSubstations(value);
                    }

                    if (layerName === "mjerna_mjesta") {
                        return [0, 155, 0, 150];
                    }

                    return [255, 165, 0, 150];
                },

                getLineColor: (feature) => {
                    const tipMreze = feature.properties.tip_mreze;

                    if (layerName === "vod") {
                        if (tipMreze === "niskonaponska") return [0, 130, 255, 200];
                        if (tipMreze === "visokonaponska") return [255, 0, 0, 200];
                        return [255, 165, 0, 200];
                    }

                    return [0, 0, 0, 255];
                },

                getLineWidth: () => {
                    const z = viewState.zoom;

                    if (layerName === "vod") {
                        if (z > 16) return 6;
                        if (z > 14) return 4;
                        if (z > 12) return 2;
                        return 1;
                    }

                    return 1;
                },

                lineWidthUnits: "pixels",
                lineWidthMinPixels: 5,
                lineWidthMaxPixels: 15,
                getPointRadius: 5,
                pointRadiusMinPixels: 5,
                pointRadiusMaxPixels: 10,
                pointRadiusUnits: "pixels",
                pickable: true,
                autoHighlight: true,
                highlightColor: [0, 0, 255, 150],
                onHover: setHoverInfo,
                onClick: handleClickInfo,
                opacity: 1,
            });
        }

        if (filterYear && transformedGeoJSON.features.length > 0) {
            const boundingBox = bbox(transformedGeoJSON);
            const transformedBoundingBox = transformBoundingBox(boundingBox);
            const boundingPolygon = bboxPolygon(transformedBoundingBox);

            const outlineLayer = new GeoJsonLayer({
                id: `${layerName}${networkType ? `-${networkType}-outline` : "-outline"}`,
                data: boundingPolygon,
                filled: false,
                stroked: true,
                getLineColor: [61, 226, 167, 255],
                getLineWidth: 13,
                originalCqlFilter,
            });

            if (setOverlay) {
                setOverlay({ active: false, message: "" });
            }

            return [layer, outlineLayer];
        }

        if (setOverlay) {
            setOverlay({ active: false, message: "" });
        }

        return [layer].filter(Boolean);
    } catch (error) {
        console.error("Greška prilikom učitavanja sloja:", error);

        if (setOverlay) {
            setOverlay({ active: false, message: "" });
        }

        showMapConfirmToast({
            title: "Greška",
            message: "Došlo je do greške prilikom učitavanja sloja.",
            confirmText: "U redu",
            cancelText: "",
            variant: "error",
            autoClose: 4000,
            showTimer: true,
        });

        return null;
    }
};

export const removeLayer = (layers, layerName, networkType) => {
    const suffix = networkType ? `-${networkType}` : "";

    return layers.filter(
        (layer) =>
            layer.id !== `${layerName}${suffix}-layer` &&
            layer.id !== `${layerName}${suffix}-outline` &&
            !layer.id.startsWith(`${layerName}${suffix}-`)
    );
};